import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, MapPin, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatErr, getPosition, uploadBlob } from "@/lib/api";

const useCamera = (open, videoRef) => {
  const [camOk, setCamOk] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => {
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        setCamOk(true);
      })
      .catch(() => setCamOk(false));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [open, videoRef]);
  return camOk;
};

export const ClockInDialog = ({ open, onOpenChange, onDone, mode }) => {
  const videoRef = useRef(null);
  const camOk = useCamera(open, videoRef);
  const [geo, setGeo] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const locate = () => {
    setGeoErr("");
    getPosition().then(setGeo).catch((e) => setGeoErr(e.message));
  };
  useEffect(() => {
    if (open) {
      setPhoto(null);
      locate();
    }
  }, [open]);

  const snap = () => {
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 480;
    c.height = v.videoHeight || 360;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => setPhoto({ blob: b, url: URL.createObjectURL(b) }), "image/jpeg", 0.85);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto({ blob: f, url: URL.createObjectURL(f) });
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const selfie_path = await uploadBlob(photo.blob, "selfie.jpg");
      const { data } = await api.post(`/attendance/clock-${mode}`, { ...geo, selfie_path });
      toast.success(mode === "in" ? "Clocked in successfully" : "Clocked out. Have a good evening!");
      onDone(data.record);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid={`clock-${mode}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading text-xl">Geo-Verified Clock {mode === "in" ? "In" : "Out"}</DialogTitle></DialogHeader>
        <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-900 relative">
          {photo ? (
            <img src={photo.url} alt="selfie" className="w-full h-full object-cover" data-testid="clock-in-selfie-preview" />
          ) : camOk === false ? (
            <label className="h-full grid place-items-center text-slate-300 text-sm cursor-pointer">
              <div className="text-center"><Upload className="h-8 w-8 mx-auto mb-2" />Camera unavailable — tap to take/upload selfie</div>
              <input type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="clock-in-selfie-file-input" />
            </label>
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
          )}
        </div>
        <div className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 border px-3 py-2" data-testid="clock-in-gps-status">
          <MapPin className={`h-4 w-4 ${geo ? "text-emerald-600" : "text-slate-400"}`} />
          {geo ? <span className="font-mono-num text-slate-700">GPS locked · {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}</span>
            : geoErr ? <span className="text-rose-600 flex-1">{geoErr}</span>
            : <span className="text-slate-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Acquiring GPS…</span>}
          {geoErr && <button onClick={locate} className="text-sky-700 text-xs font-semibold" data-testid="clock-in-retry-gps">Retry</button>}
        </div>
        <div className="flex gap-2">
          {photo ? (
            <Button variant="outline" className="flex-1" onClick={() => setPhoto(null)} data-testid="clock-in-retake-button"><RefreshCw className="h-4 w-4 mr-2" />Retake</Button>
          ) : (
            <Button variant="outline" className="flex-1" onClick={snap} disabled={!camOk} data-testid="clock-in-capture-button"><Camera className="h-4 w-4 mr-2" />Capture Selfie</Button>
          )}
          <Button className={`flex-1 ${mode === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} disabled={!photo || !geo || busy} onClick={confirm} data-testid={`clock-${mode}-confirm-button`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm Clock ${mode === "in" ? "In" : "Out"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
